'use client';

import Icon from '@/components/ui/AppIcon';

interface DataCollectionSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function DataCollectionSection({ isExpanded, onToggle }: DataCollectionSectionProps) {
  const dataTypes = [
    {
      icon: 'UserIcon',
      title: 'Personal Information',
      description: 'Name, email address, phone number, date of birth, and identification documents',
      details: 'We collect this information when you create an account, make a booking, or contact us. This data is essential for identity verification, reservation processing, and communication purposes.'
    },
    {
      icon: 'CalendarIcon',
      title: 'Booking Details',
      description: 'Check-in/out dates, apartment preferences, special requests, and guest count',
      details: 'Booking information helps us prepare your accommodation, fulfill special requests, and provide personalized service during your stay. We retain this data for service improvement and legal compliance.'
    },
    {
      icon: 'CreditCardIcon',
      title: 'Payment Information',
      description: 'Credit card details, billing address, and transaction history',
      details: 'Payment data is processed through secure, PCI-DSS compliant payment gateways. We do not store complete credit card numbers on our servers. Only tokenized payment information is retained for authorized transactions.'
    },
    {
      icon: 'ChartBarIcon',
      title: 'Behavioral Analytics',
      description: 'Website interactions, search queries, device information, and IP addresses',
      details: 'We use cookies and analytics tools to understand how you interact with our website. This helps us improve user experience, optimize our services, and provide relevant content. You can control cookie preferences through your browser settings.'
    }
  ];

  return (
    <section id="data-collection" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="DocumentTextIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Data Collection</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              What Information We Collect
            </h2>
            <p className="text-lg text-text-secondary">
              We collect only the information necessary to provide you with exceptional service and comply with legal requirements.
            </p>
          </div>

          <div className="space-y-6">
            {dataTypes.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-card border border-border overflow-hidden transition-all duration-300 hover:shadow-elevated"
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={item.icon as any} size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-text-primary mb-2">
                        {item.title}
                      </h3>
                      <p className="text-text-secondary mb-3">
                        {item.description}
                      </p>
                      <button
                        onClick={onToggle}
                        className="text-primary hover:text-primary/80 font-medium flex items-center space-x-1 transition-colors"
                      >
                        <span>{isExpanded ? 'Show Less' : 'Learn More'}</span>
                        <Icon 
                          name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
                          size={16} 
                          className="text-current" 
                        />
                      </button>
                      {isExpanded && (
                        <div className="mt-4 p-4 bg-muted rounded-lg animate-scale-in">
                          <p className="text-text-secondary text-sm leading-relaxed">
                            {item.details}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-accent/10 rounded-xl border border-accent/20">
            <div className="flex items-start space-x-3">
              <Icon name="InformationCircleIcon" size={24} className="text-accent flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Consent Mechanism</h4>
                <p className="text-text-secondary text-sm">
                  By using our services, you consent to the collection and processing of your personal information as described in this policy. You can withdraw consent at any time by contacting our Data Protection Officer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}